import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  FormControl,
  Grid,
  Pagination,
  Table,
} from "react-bootstrap";
import NotificationSystem from "react-notification-system";

import { getClaimExtractions } from "api/claimExtraction/claimExtraction";
import { getApiErrorMessage } from "utils/getApiErrorMessage";
import LoadingSpinner from "views/Components/loadingSpinner";
import "./claimExtractionList.css";

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const PAGE_WINDOW_SIZE = 5;

const EXTRACTION_COLUMNS = [
  { label: "Claim ID", key: "claim_id" },
  { label: "Patient Name", key: "patient_name" },
  { label: "Patient Phone", key: "patient_phone" },
  { label: "Patient Mail", key: "patient_mail" },
  { label: "Sickness", key: "sickness" },
  { label: "Doctor Name", key: "doctor_name" },
  { label: "Facility Name", key: "facility_name" },
  { label: "Facility Phone", key: "facility_phone" },
  { label: "Facility Mail", key: "facility_mail" },
  { label: "Location", key: "location" },
  { label: "Prescription Items", key: "prescription_items_text" },
];

const COMPLETENESS_FIELDS = EXTRACTION_COLUMNS.map((column) => column.key);

const getSortableValue = (row) => {
  const timestampSource =
    row.created_at ||
    row.updated_at ||
    row.extracted_at ||
    row.createdAt ||
    row.updatedAt;

  if (timestampSource) {
    const timestamp = new Date(timestampSource).getTime();
    if (!Number.isNaN(timestamp)) {
      return timestamp;
    }
  }

  const numericId = Number(row.id);
  return Number.isNaN(numericId) ? 0 : numericId;
};

const sortLatestFirst = (rows) => {
  return [...rows].sort((left, right) => {
    return getSortableValue(right) - getSortableValue(left);
  });
};

const getPageNumbers = (currentPage, lastPage) => {
  if (lastPage <= PAGE_WINDOW_SIZE) {
    return Array.from({ length: lastPage }, (_, index) => index + 1);
  }

  let startPage = Math.max(1, currentPage - Math.floor(PAGE_WINDOW_SIZE / 2));
  let endPage = startPage + PAGE_WINDOW_SIZE - 1;

  if (endPage > lastPage) {
    endPage = lastPage;
    startPage = lastPage - PAGE_WINDOW_SIZE + 1;
  }

  return Array.from(
    { length: endPage - startPage + 1 },
    (_, index) => startPage + index,
  );
};

const isValuePresent = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.some((item) => isValuePresent(item));
  }

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  return true;
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }

  return value;
};

const getPrimaryTimestamp = (row) => {
  return (
    row.created_at ||
    row.updated_at ||
    row.extracted_at ||
    row.createdAt ||
    row.updatedAt ||
    ""
  );
};

const formatDateTime = (value) => {
  if (!value) {
    return "No timestamp";
  }

  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) {
    return value;
  }

  return dateValue.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getCompletionMeta = (row) => {
  const missingLabels = EXTRACTION_COLUMNS.filter(
    (column) => !isValuePresent(row[column.key]),
  ).map((column) => column.label);
  const completedFields = COMPLETENESS_FIELDS.length - missingLabels.length;
  const ratio = Math.round(
    (completedFields / COMPLETENESS_FIELDS.length) * 100,
  );

  return {
    completedFields,
    missingLabels,
    ratio,
    totalFields: COMPLETENESS_FIELDS.length,
  };
};

const getCompletionTone = (ratio) => {
  if (ratio >= 90) {
    return "strong";
  }

  if (ratio >= 70) {
    return "moderate";
  }

  return "attention";
};

const getSummaryPreview = (value) => {
  const textValue = String(formatCellValue(value))
    .replace(/\s+/g, " ")
    .trim();

  if (textValue.length <= 180) {
    return textValue;
  }

  return `${textValue.slice(0, 177)}...`;
};

const getColumnCellClassName = (columnKey) => {
  if (columnKey === "prescription_items_text") {
    return "claim-extraction-table__cell claim-extraction-table__cell--prescription";
  }

  return "claim-extraction-table__cell";
};

const ClaimExtractionList = (props) => {
  const notificationRef = useRef(null);
  const storedUserRef = useRef(JSON.parse(localStorage.getItem("user") || "null"));
  const storedUser = storedUserRef.current;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!storedUser) {
      props.history.push("/auth/login-page");
      return;
    }

    if (Number(storedUser.role) !== 1) {
      props.history.push("/admin/dashboard");
    }
  }, [props.history, storedUser]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      setSearchText(searchInput.trim());
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    if (!storedUser || Number(storedUser.role) !== 1) {
      return;
    }

    const fetchClaimExtractions = async () => {
      setLoading(true);

      try {
        const response = await getClaimExtractions(storedUser.token, {
          page: currentPage,
          pageSize,
          searchText,
        });

        if (!response.success) {
          throw new Error(response.message || "Failed to load extractions");
        }

        const pagination = response.data || {};
        const extractionRows = Array.isArray(pagination.data)
          ? pagination.data
          : [];

        setRows(sortLatestFirst(extractionRows));
        setCurrentPage(Number(pagination.current_page) || 1);
        setLastPage(Number(pagination.last_page) || 1);
        setPageSize(Number(pagination.per_page) || pageSize);
        setTotal(Number(pagination.total) || 0);
      } catch (error) {
        setRows([]);
        setTotal(0);
        setLastPage(1);

        const message = getApiErrorMessage(error);
        if (notificationRef.current) {
          notificationRef.current.addNotification({
            message,
            level: "error",
            position: "tr",
            autoDismiss: 8,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchClaimExtractions();
  }, [currentPage, pageSize, reloadKey, searchText, storedUser]);

  const handleReset = () => {
    setSearchInput("");
    setSearchText("");
    setCurrentPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
  };

  const pageNumbers = getPageNumbers(currentPage, lastPage);
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(currentPage * pageSize, total);

  return (
    <div className="main-content claim-extraction-page">
      <NotificationSystem ref={notificationRef} />
      <Grid fluid>
        <div className="claim-extraction-shell">
          <section className="claim-extraction-controls">
            <div className="claim-extraction-controls__header">
              <div>
                <h2>Review Controls</h2>
                <p>Search the queue, adjust table density, and reset the view.</p>
              </div>
              <div className="claim-extraction-controls__status">
                {searchText ? (
                  <span className="claim-extraction-controls__status-chip">
                    Search: {searchText}
                  </span>
                ) : (
                  <span className="claim-extraction-controls__status-chip">
                    Full queue
                  </span>
                )}
              </div>
            </div>
            <div className="claim-extraction-controls__toolbar">
              <div className="claim-extraction-controls__search">
                <label
                  className="claim-extraction-field-label"
                  htmlFor="claim-extraction-search"
                >
                  Search records
                </label>
                <div className="claim-extraction-search-wrap">
                  <i className="fa fa-search claim-extraction-search-wrap__icon" />
                  <FormControl
                    id="claim-extraction-search"
                    type="text"
                    value={searchInput}
                    placeholder="Search by claim, patient, provider, or extraction text"
                    onChange={(event) => setSearchInput(event.target.value)}
                  />
                </div>
              </div>
              <div className="claim-extraction-controls__page-size">
                <label
                  className="claim-extraction-field-label"
                  htmlFor="claim-extraction-page-size"
                >
                  Rows per page
                </label>
                <FormControl
                  id="claim-extraction-page-size"
                  componentClass="select"
                  value={pageSize}
                  onChange={(event) => {
                    setCurrentPage(1);
                    setPageSize(Number(event.target.value));
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option} per page
                    </option>
                  ))}
                </FormControl>
              </div>
              <div className="claim-extraction-controls__actions-group">
                <label className="claim-extraction-field-label">
                  Actions
                </label>
                <div className="claim-extraction-actions">
                  <Button
                    className="claim-extraction-button claim-extraction-button--quiet"
                    onClick={() => setReloadKey((previousValue) => previousValue + 1)}
                    disabled={loading}
                  >
                    <i className="fa fa-refresh" aria-hidden="true" />
                    <span>Refresh</span>
                  </Button>
                  <Button
                    className="claim-extraction-button claim-extraction-button--primary"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    <i className="fa fa-undo" aria-hidden="true" />
                    <span>Reset</span>
                  </Button>
                </div>
              </div>
              <div className="claim-extraction-controls__range-group">
                <div className="claim-extraction-range">
                  <span className="claim-extraction-range__label">Showing</span>
                  <div className="claim-extraction-range__value">
                    <strong>
                      {rangeStart}-{rangeEnd}
                    </strong>
                    <span className="claim-extraction-range__suffix">
                      of {total}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="claim-extraction-table-card">
            <div className="claim-extraction-table-wrap">
              <Table className="claim-extraction-table">
                <thead>
                  <tr>
                    {EXTRACTION_COLUMNS.map((column) => (
                      <th key={column.key}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={EXTRACTION_COLUMNS.length}
                        className="claim-extraction-state-cell"
                      >
                        <LoadingSpinner />
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={EXTRACTION_COLUMNS.length}
                        className="claim-extraction-state-cell"
                      >
                        <div className="claim-extraction-empty-state">
                          <i className="fa fa-file-text-o" />
                          <h3>No extraction records found</h3>
                          <p>
                            Try a different search term or reset the filters to
                            load the full review queue.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => {
                      const completion = getCompletionMeta(row);
                      const completionTone = getCompletionTone(completion.ratio);

                      return (
                        <tr
                          className={`claim-extraction-table__row claim-extraction-table__row--${completionTone}`}
                          key={row.id || `${row.patient_name || "row"}-${index}`}
                        >
                          {EXTRACTION_COLUMNS.map((column) => (
                            <td
                              key={column.key}
                              className={`${getColumnCellClassName(column.key)}${
                                column.key === "claim_id"
                                  ? " claim-extraction-table__cell--sticky"
                                  : ""
                              }`}
                              title={String(formatCellValue(row[column.key]))}
                            >
                              {column.key === "claim_id" ? (
                                <>
                                  <div className="claim-extraction-cell__primary">
                                    {formatCellValue(row[column.key])}
                                  </div>
                                  <div className="claim-extraction-cell__secondary">
                                    {formatDateTime(getPrimaryTimestamp(row))}
                                  </div>
                                  {isValuePresent(row.id) ? (
                                    <div className="claim-extraction-cell__tertiary">
                                      Record ID: {row.id}
                                    </div>
                                  ) : null}
                                </>
                              ) : column.key === "prescription_items_text" ? (
                                <div className="claim-extraction-prescription">
                                  {getSummaryPreview(row[column.key])}
                                </div>
                              ) : (
                                <div className="claim-extraction-table__text">
                                  {formatCellValue(row[column.key])}
                                </div>
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </section>

          <section className="claim-extraction-pagination-bar">
            <div className="claim-extraction-pagination-bar__summary">
              Page {currentPage} of {lastPage}
            </div>
            <Pagination className="claim-extraction-pagination">
              <Pagination.First
                onClick={() => setCurrentPage(1)}
                disabled={currentPage <= 1 || loading}
              />
              <Pagination.Prev
                onClick={() =>
                  setCurrentPage((previousPage) =>
                    Math.max(previousPage - 1, 1),
                  )
                }
                disabled={currentPage <= 1 || loading}
              />
              {pageNumbers[0] > 1 && <Pagination.Ellipsis disabled />}
              {pageNumbers.map((pageNumber) => (
                <Pagination.Item
                  key={pageNumber}
                  active={pageNumber === currentPage}
                  onClick={() => setCurrentPage(pageNumber)}
                >
                  {pageNumber}
                </Pagination.Item>
              ))}
              {pageNumbers[pageNumbers.length - 1] < lastPage && (
                <Pagination.Ellipsis disabled />
              )}
              <Pagination.Next
                onClick={() =>
                  setCurrentPage((previousPage) =>
                    Math.min(previousPage + 1, lastPage),
                  )
                }
                disabled={currentPage >= lastPage || loading}
              />
              <Pagination.Last
                onClick={() => setCurrentPage(lastPage)}
                disabled={currentPage >= lastPage || loading}
              />
            </Pagination>
          </section>
        </div>
      </Grid>
    </div>
  );
};

export default ClaimExtractionList;
